import { joiResolver } from '@hookform/resolvers/joi';
import { Checkbox } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { FieldError, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ApiButton,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  ErrorInfo,
  Typography,
  useSnackbarHandler,
} from 'web-library';
import { usePostExamFee } from '../../../../api-refactored/exam-configuration/usePostExamFee';
import {
  usePostExamFeeAdmissionLevel
} from '../../../../api-refactored/exam-configuration/usePostExamFeeAdmissionLevel';
import {
  ExamFeeModelResponse
} from '../../../../api-refactored/exam-configuration/usePostExamFeeListing';
import { usePutExamFee } from '../../../../api-refactored/exam-configuration/usePutExamFee';
import { KeyValueResponse } from '../../../../api-refactored/typings';
import ControlledAutocomplete from '../../../../components/form-items/ControlledAutocomplete';
import ToggleDisplayEditFormItem from '../../../../components/ToggleDisplayEditFormItem';
import { concatStringsWithComma } from '../../../../utils/StringUtils';
import GstFormSection from './GstFormSection';
import RegistrationFeeFormSection from './RegistrationFeeFormSection';
import { AeisExamFeeFormData, AeisExamFeeSchema } from './schema/AeisExamFeeSchema';

const useStyles = makeStyles()((theme) =>
({
  actionContainer: {
    paddingRight: theme.spacing(1),
    paddingBottom: theme.spacing(2),
  },
  title: {
    color: theme.color.text.textPrimary,
    paddingBottom: theme.spacing(3),
  },
  formFieldContainer: {
    paddingBottom: theme.spacing(3),
  },
  dividerContainer: {
    paddingBottom: theme.spacing(3),
  },
}));

interface AeisExamFeeModalProps {
  registrationExamLevelUuid: string;
  year: number;
  isEditState: boolean;
  isModalOpened: boolean;
  setModalOpened: Dispatch<SetStateAction<boolean>>;
  fetchExamFeeListing: () => void;
  defaultData?: ExamFeeModelResponse;
}

const AeisExamFeeModal = ({
  registrationExamLevelUuid,
  year,
  isEditState,
  isModalOpened,
  setModalOpened,
  fetchExamFeeListing,
  defaultData,
}: AeisExamFeeModalProps) => {
  const { t } = useTranslation();
  const { snackbarHandler } = useSnackbarHandler();
  const { classes } = useStyles();
  const [admissionLevel, setAdmissionLevel] = useState<KeyValueResponse[]>([]);
  const [apiErrorMsg, setApiErrorMsg] = useState<string | null>(null);

  const getFormDefaultValue = () => {
    let defaultGstInclusive = 'Y';
    if (defaultData) {
      defaultGstInclusive = defaultData.gstInclusive ? 'Y' : 'N';
    }
    return {
      gstPercent: defaultData?.gstPercent,
      gstInclusive: defaultGstInclusive,
      registrationFee: defaultData?.registrationFee,
      lateRegistrationFee: defaultData?.lateRegistrationFee,
      admissionLevelCodes: defaultData?.admissionLevelCode
        ? new Array({ code: defaultData?.admissionLevelCode })
        : [],
    };
  };

  const formHook = useForm<AeisExamFeeFormData>({
    mode: 'onSubmit',
    resolver: joiResolver(AeisExamFeeSchema),
    defaultValues: getFormDefaultValue(),
  });
  const { handleSubmit, control, formState: { errors }, reset: formReset, getValues } = formHook;

  const { mutate: admissionLevelMutate } = usePostExamFeeAdmissionLevel({
    onSuccess: (data) => {
      setAdmissionLevel(data);
    },
  });

  const {
    mutate: createExamFeeMutate,
    reset: createExamFeeReset,
    status: createExamFeeStatus,
  } = usePostExamFee({
    onSuccess: () => {
      fetchExamFeeListing();
      const admissionLevels = getValues('admissionLevelCodes').map((v) => v.description);
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('exam->>ExamFeeConfiguration.AeisFeeAdded', {
          admissionLevel: concatStringsWithComma(admissionLevels),
        }),
      });
      onDialogCloseHandler();
    },
    onError: (err) => {
      setApiErrorMsg(err.response?.data.message!);
    },
  });
  const {
    mutate: updateExamFeeMutate,
    reset: updateExamFeeReset,
    status: updateExamFeeStatus,
  } = usePutExamFee({
    onSuccess: () => {
      fetchExamFeeListing();
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('exam->>ExamFeeConfiguration.AeisFeeUpdated', {
          admissionLevel: defaultData?.admissionLevelDescription,
        }),
      });
      onDialogCloseHandler();
    },
    onError: (err) => {
      setApiErrorMsg(err.response?.data.message!);
    },
  });

  useEffect(() => {
    // Update form default value each time the modal is opened.
    if (isModalOpened) {
      formReset(getFormDefaultValue());
    }

    // Update the admission level list each time "create" modal is opened.
    if (isModalOpened && !isEditState) {
      admissionLevelMutate({ registrationExamLevelUuid, year });
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isModalOpened]);

  const onDialogCloseHandler = () => {
    setModalOpened(false);
    formReset();
    createExamFeeReset();
    updateExamFeeReset();
    setApiErrorMsg(null);
  };

  const onSubmitHandler = (formData: AeisExamFeeFormData) => {
    setApiErrorMsg(null);
    const baseRequest = {
      registrationFee: formData.registrationFee,
      gstInclusive: formData.gstInclusive === 'Y',
      gstPercent: formData.gstPercent,
      lateRegistrationFee: formData.lateRegistrationFee,
    };

    if (isEditState) {
      updateExamFeeMutate({ ...baseRequest, examFeeUuid: defaultData?.uuid || '' });
    } else {
      createExamFeeMutate({
        ...baseRequest,
        registrationExamLevelUuid,
        year,
        admissionLevelCodes: formData.admissionLevelCodes.map((v) => v.code),
      });
    }
  };

  return (
    <Dialog id="aeis-exam-fee-modal" open={isModalOpened} maxWidth="md">
      {isEditState ? (
        <DialogTitle>{t('exam->>ExamFeeConfiguration.EditExamFees')}</DialogTitle>
      ) : (
        <DialogTitle>{t('exam->>ExamFeeConfiguration.AddExamFees')}</DialogTitle>
      )}

      <DialogContent>
        <form id="aeis-fee-form" onSubmit={handleSubmit(onSubmitHandler)}>
          {!isEditState && (
            <Typography variant="h3" className={classes.title}>
              {t('exam->>ExamConfiguration.SelectAdmissionLevel')}
            </Typography>
          )}
          <ToggleDisplayEditFormItem
            containerClassName={!isEditState ? classes.formFieldContainer : ''}
            isEditState={!isEditState}
            labelText={t('exam->>ExamConfiguration.AdmissionLevel')}
            valueText={defaultData?.admissionLevelDescription}
            EditComponent={
              <ControlledAutocomplete<KeyValueResponse, true, false, undefined>
                id="inputSelect-admissionLevel"
                label={t('exam->>ExamConfiguration.AdmissionLevel')}
                name="admissionLevelCodes"
                placeholder={t('exam->>ExamFeeConfiguration.AdmissionLevelPlaceHolder')}
                control={control}
                options={admissionLevel}
                error={!!errors.admissionLevelCodes}
                helperText={((errors.admissionLevelCodes as unknown) as FieldError)?.message?.toString()}
                getOptionLabel={(option: KeyValueResponse) => option.description}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                disableCloseOnSelect
                multiple
                isRequired
                renderOption={(_, option: KeyValueResponse, { selected }, __) => (
                  <>
                    <Checkbox checked={selected} />
                    {option.description}
                  </>
                )}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, tagIndex) => (
                    <Chip
                      id="auto-complete"
                      label={option.description}
                      {...getTagProps({ index: tagIndex })}
                    />
                  ))
                }
              />
            }
          />
          {!isEditState && <Divider className={classes.dividerContainer} />}
          <RegistrationFeeFormSection
            title={t('exam->>ExamFeeConfiguration.ExamFees')}
            formHook={formHook}
            isModalOpened={isModalOpened}
            defaultData={defaultData}
          />
          <Divider className={classes.dividerContainer} />
          <GstFormSection
            formHook={formHook}
            isModalOpened={isModalOpened}
            defaultData={defaultData}
          />
        </form>
        {apiErrorMsg && <ErrorInfo label={apiErrorMsg} />}
      </DialogContent>
      <DialogActions>
        <Box className={classes.actionContainer}>
          <Button type="button" variant="text" onClick={onDialogCloseHandler}>
            {t('exam->>ExamConfiguration.Cancel')}
          </Button>
          <ApiButton
            type="submit"
            status={isEditState ? updateExamFeeStatus : createExamFeeStatus}
            form="aeis-fee-form"
          >
            {t('exam->>ExamFeeConfiguration.ConfirmExamFees')}
          </ApiButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AeisExamFeeModal;
